import { List } from 'immutable';
import { DateTime } from 'luxon';
import type { AnyAction } from 'redux-saga';
import { put, select, take, takeLatest, takeLeading } from 'redux-saga/effects';

import { transactionsActions, transactionsTypes } from 'app-components/flux/transactions';

import { accountsActions, accountsSelectors, accountsTypes } from '../accounts';
import type { VehicleAccountProps } from '../accounts/accountsTypes';
import type { CoreAction } from '../core/resourceStoreTypes';
import { institutionLoginsActions, institutionLoginsTypes } from '../institution-logins';
import { saveKbbVehicleAccount, saveManualVehicleAccount, updateVehicleAccountFields } from './vehicleActions';
import { getKelleyBlueBookFIId } from './vehicleSelectors';
import type { KbbVehicleSubmittingObject } from './vehicleTypes';

export function* saveKbbVehicleAccountActionWatcher() {
  yield takeLatest(
    saveKbbVehicleAccount.toString(),
    function* handleSaveKbbVehicle(action: {
      meta: any;
      payload: KbbVehicleSubmittingObject;
      type: 'SAVE_KBB_VEHICLE_ACCOUNT';
    }) {
      const {
        year,
        makeId,
        make,
        modelId,
        model,
        trimId,
        trim,
        condition,
        mileage,
        zipCode,
        vehicleId,
        scope,
        linkTo,
        businessId,
        name,
        autoUpdateMiles,
        useCalculatedMilesPerMonth,
        customMilesPerMonth,
      } = action.payload;

      const isNewAccount = linkTo === '0';

      let institutionLoginId: string | undefined = yield select(getKelleyBlueBookFIId);

      if (!institutionLoginId) {
        const institutionLoginToUpsert = institutionLoginsTypes.mkInstitutionLoginClient({
          clientId: scope,
          cpSetupMode: 'SKIP_ACCOUNT_DISCOVERY',
          channel: 'KELLEY_BLUE_BOOK',
          name: 'Kelley Blue Book',
        });

        yield put(
          institutionLoginsActions.addInstitutionLoginAndAccounts(
            { institutionLogin: institutionLoginToUpsert, accounts: [] },
            { scope },
          ),
        );

        const institutionLoginIdResponse: CoreAction<any, any> = yield take(
          institutionLoginsActions.addInstitutionLoginAndAccountsResponse,
        );
        institutionLoginId = institutionLoginIdResponse.payload.institutionLogin.id;
      }

      const aggregators = List([
        accountsTypes.mkAccountAggregator({
          channel: 'KELLEY_BLUE_BOOK',
          cpId: vehicleId.toString(),
        }),
      ]);

      const yearNumber = Number(year);
      const mileageNumber = Number(mileage);

      const accountParams: Partial<VehicleAccountProps> = {
        aggregators,
        clientId: scope,
        institutionLoginId,
        name: name,
        type: 'VEHICLE',
        year: !Number.isNaN(yearNumber) && Number.isFinite(yearNumber) ? yearNumber : null,
        makeId: makeId,
        make: make,
        modelId: modelId,
        model: model,
        trimId: trimId,
        trim: trim,
        mileage: !Number.isNaN(mileageNumber) && Number.isFinite(mileageNumber) ? mileageNumber : null,
        zipCode: zipCode,
        businessId: businessId,
        usageType: businessId ? 'BUSINESS' : 'PERSONAL',
        condition: condition,
        autoUpdateMiles: autoUpdateMiles ?? false,
        useCalculatedMilesPerMonth: useCalculatedMilesPerMonth ?? false,
        customMilesPerMonth: customMilesPerMonth,
        isExcludedFromBudgets: true,
        isExcludedFromReports: true,
      };

      if (isNewAccount) {
        const accountToUpsert = accountsTypes.mkAccount('VEHICLE', accountParams);
        yield put(accountsActions.createAccount(accountToUpsert, { scope }));
        yield take(accountsActions.createAccountSuccess);
      } else {
        const accountToUpsert = accountsTypes.mkUpdateAccount('VEHICLE', {
          ...accountParams,
          id: linkTo,
        });
        yield put(accountsActions.updateAccount(accountToUpsert, { scope }));
        yield take(accountsActions.updateAccountSuccess);
      }

      if (institutionLoginId) {
        yield put(institutionLoginsActions.refreshAccounts({ institutionLoginId }));
      }
    },
  );
}

export function* saveManualVehicleAccountActionWatcher() {
  yield takeLatest(saveManualVehicleAccount, function* handleSaveManualVehicle(action) {
    const { name, estimate, scope, businessId, linkTo } = action.payload;

    const isNewAccount = linkTo === '0';

    const accountParams: Partial<VehicleAccountProps> = {
      clientId: scope,
      institutionLoginId: undefined,
      isExcludedFromAccountBar: false,
      isExcludedFromBudgets: true,
      isExcludedFromReports: true,
      name: name.trim(),
      type: 'VEHICLE',
      businessId: businessId,
      usageType: businessId ? 'BUSINESS' : 'PERSONAL',
    };

    let accountId: string | null | undefined;

    if (isNewAccount) {
      const accountToUpsert = accountsTypes.mkAccount('VEHICLE', accountParams);
      yield put(accountsActions.createAccount(accountToUpsert));

      const accountCreationResponse: AnyAction = yield take(accountsActions.createAccountSuccess);
      const newAccount: accountsTypes.VehicleAccount = accountCreationResponse?.payload;
      accountId = newAccount?.id;
    } else {
      // Clear any existing connection
      const existingAccount: accountsTypes.VehicleAccount | undefined = yield select(
        accountsSelectors.getAccountById,
        linkTo,
      );
      const hasInstitutionLogin = Boolean(
        existingAccount?.institutionLoginId && existingAccount.institutionLoginId !== '0',
      );
      const hasKbbAggregator = Boolean(
        existingAccount?.aggregators?.some((aggregator) => aggregator.channel === 'KELLEY_BLUE_BOOK'),
      );

      if (hasInstitutionLogin || hasKbbAggregator) {
        // make sure to clear any connection the account may have had before
        yield put(accountsActions.performAccountAction({ id: linkTo, qcsAction: 'make-manual' }));
        yield take(accountsActions.performAccountActionSuccess);
      }

      const accountToUpsert = accountsTypes.mkUpdateAccount('VEHICLE', {
        ...accountParams,
        id: linkTo,
      });
      yield put(accountsActions.updateAccount(accountToUpsert, { scope }));
      yield take(accountsActions.updateAccountSuccess);
      accountId = linkTo;
    }

    const newEstimate = Number(estimate) || 0.0;
    const balanceAsOfOn = DateTime.local().toISODate() || '1970-01-01';

    let adjustmentAmount: number;
    if (isNewAccount) {
      adjustmentAmount = newEstimate;
    } else {
      // make an adjustment based on the total value of the account
      const existingAccountForBalance: accountsTypes.VehicleAccount | undefined = yield select(
        accountsSelectors.getAccountById,
        linkTo,
      );
      const currentBalance = existingAccountForBalance?.currentBalanceAsOf ?? 0;
      adjustmentAmount = newEstimate - currentBalance;
    }

    if (adjustmentAmount !== 0 && accountId) {
      const newTxn = new transactionsTypes.CashFlowTransaction({
        accountId: accountId,
        payee: `${name.trim()}`,
        coa: {
          type: 'BALANCE_ADJUSTMENT',
          id: '0',
        },
        state: 'CLEARED',
        isReviewed: true,
        amount: adjustmentAmount,
        postedOn: balanceAsOfOn,
        clientId: scope,
        isExcludedFromReports: true,
        isExcludedFromF2S: true,
        isBill: false,
        isSubscription: false,
      });
      yield put(transactionsActions.createTransaction(newTxn, { context: 'addAccounts' }));
    }
  });
}

export function* updateVehicleAccountFieldsWatcher() {
  yield takeLeading(updateVehicleAccountFields, function* handleUpdateFields(action) {
    const { name, businessId, linkTo, scope, autoUpdateMiles, useCalculatedMilesPerMonth, customMilesPerMonth } =
      action.payload;

    if (!linkTo || linkTo === '0') {
      return;
    }

    const accountParams: Partial<VehicleAccountProps> = {
      id: linkTo,
      clientId: scope,
      name: name.trim(),
      businessId: businessId,
      usageType: businessId ? 'BUSINESS' : 'PERSONAL',
      autoUpdateMiles: autoUpdateMiles ?? false,
      useCalculatedMilesPerMonth: useCalculatedMilesPerMonth ?? false,
      customMilesPerMonth: customMilesPerMonth,
    };

    const updateAccount = accountsTypes.mkUpdateAccount('VEHICLE', accountParams);
    yield put(accountsActions.updateAccount(updateAccount));
  });
}

export default [
  saveManualVehicleAccountActionWatcher,
  saveKbbVehicleAccountActionWatcher,
  updateVehicleAccountFieldsWatcher,
];
